import { useTheme } from '@/app/ThemeContext';
import { Modal } from '@/components/Modal';
import { useModifyAccount } from '@/hooks/useAccounts';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { AllIntegrations } from '@/hooks/useIntegration';
import { Account } from '@/types';

export interface DisconnectIntegrationModalProps {
  /** The connected account to disconnect. When null, the modal is closed. */
  account: Account | null;
  /** Called to close the modal (both on cancel and after a successful disconnect). */
  onClose: () => void;
  /** Optional impersonation email, forwarded to the unlink mutation. */
  email?: string;
  /** Optional hook fired after a successful unlink (e.g. to invalidate scoped queries). */
  onDisconnected?: () => void;
}

/**
 * Shared confirmation modal for disconnecting a connected integration account.
 *
 * Encapsulates the same flow used on the `/integrations` page so the integration
 * drawer cards and the integrations table disconnect identically.
 */
export const DisconnectIntegrationModal = ({
  account,
  onClose,
  email,
  onDisconnected,
}: DisconnectIntegrationModalProps) => {
  const { currentTheme } = useTheme();
  const { track } = useAnalytics();
  const { mutateAsync: unlink } = useModifyAccount('unlink', email);

  return (
    <Modal
      open={Boolean(account)}
      onClose={onClose}
      bodyClassName="pt-4"
      title={
        <span className="leading-normal">
          Are you sure you want to disconnect this integration?
        </span>
      }
      footer={{
        baseClassName: 'flex justify-end gap-3',
        text: 'Disconnect',
        onClick: async () => {
          if (!account) return;
          await unlink({
            config: account.config,
            key: account.key,
            member: account.member,
            value: account.value ?? '',
            username: account.member,
          });
          track(AnalyticsEvent.INTEGRATION_DISCONNECT, {
            integration: account.member,
          });
          onDisconnected?.();
          onClose();
        },
      }}
    >
      <div className="flex flex-col gap-4">
        <p className="text-chariot-text-secondary -mt-2 text-sm">
          This action will disconnect the integration below.
        </p>
        <div className="bg-chariot-background-secondary flex flex-col gap-2 rounded-lg p-4">
          <div className="flex flex-row items-center gap-2 text-sm font-bold">
            <img
              src={`/icons/${currentTheme}/${
                AllIntegrations[account?.member as keyof typeof AllIntegrations]
                  ?.logo
              }`}
              alt={account?.name}
              className="m-2 size-10"
            />
            <div className="flex flex-col gap-2">
              <p>Integration</p>
              <p>{account?.displayName}</p>
            </div>
          </div>
          <p>Identifier: {account?.value}</p>
        </div>
      </div>
    </Modal>
  );
};
